import dayjs from 'dayjs';
import { ScheduleAvailabilityByDayOfWeek, Weekday, Schedule } from '@frontend/api-schedule-availability';
import { i18next } from '@frontend/i18n';
import { FormValues } from '@frontend/design-system';
import { DAYS_OF_WEEK } from './constants';
import { AvailabilityHoursFormConfig, BreaksByDay } from './types';

export const prepareFormValuesFromScheduleInfo = (
  scheduleInfo?: ScheduleAvailabilityByDayOfWeek
): FormValues<AvailabilityHoursFormConfig> => {
  const formValues: FormValues<AvailabilityHoursFormConfig> = {};
  DAYS_OF_WEEK.forEach((day) => {
    const daySchedule = scheduleInfo?.[day] || [];
    const startTime = daySchedule[0]?.startTime || '';
    const endTime = daySchedule[0]?.endTime || '';
    const hasWorkingHours = !!startTime && !!endTime;
    formValues[`${day}-checkbox`] = hasWorkingHours;
    formValues[`${day}-time-range`] = hasWorkingHours ? [startTime, endTime] : ['', ''];
  });
  return formValues;
};

export const getAvailabilityHoursFormFields = (): AvailabilityHoursFormConfig => {
  return DAYS_OF_WEEK.reduce((acc, day) => {
    acc[`${day}-checkbox`] = {
      type: 'checkbox',
      value: false,
    };

    acc[`${day}-time-range`] = {
      type: 'timeRange',
      value: ['', ''],
      interval: 15,
      validator: ({ value }, allFields) => {
        if (!allFields?.[`${day}-checkbox`]?.value) return '';

        const [startTime, endTime] = value;
        if (!startTime || !endTime) return i18next.t('Please select a start and end time.');

        const startTimeInSeconds = timeToSeconds(startTime);
        const endTimeInSeconds = timeToSeconds(endTime);
        if (startTimeInSeconds === null || endTimeInSeconds === null || startTimeInSeconds >= endTimeInSeconds) {
          return i18next.t('Please select a later end time.');
        }

        return '';
      },
    };

    return acc;
  }, {} as AvailabilityHoursFormConfig);
};

const timeToSeconds = (time: string): number | null => {
  const [hours, minutes, seconds] = time.split(':').map(Number);
  if (
    !Number.isInteger(hours) ||
    !Number.isInteger(minutes) ||
    !Number.isInteger(seconds) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59 ||
    seconds < 0 ||
    seconds > 59
  ) {
    return null;
  }

  return hours * 60 * 60 + minutes * 60 + seconds;
};

export const addSecondsToTimeFormat = (time: string): string => dayjs(time, 'HH:mm').format('HH:mm:ss');
export const removeSecondsFromTimeFormat = (time: string): string => dayjs(time, 'HH:mm:ss').format('HH:mm');

export const convertScheduleToBreaksByDay = (scheduleInfo?: ScheduleAvailabilityByDayOfWeek): BreaksByDay => {
  return DAYS_OF_WEEK.reduce((acc, day) => {
    const daySchedule = scheduleInfo?.[day] || [];
    const hasWorkingHours = !!daySchedule[0]?.startTime && !!daySchedule[0]?.endTime;
    const breaks = hasWorkingHours ? daySchedule[0]?.breaks || [] : [];

    acc[day] = breaks.map((b) => [addSecondsToTimeFormat(b.startTime), addSecondsToTimeFormat(b.endTime)]);
    return acc;
  }, {} as BreaksByDay);
};

export const getScheduleFromAvailabilityHoursFormValues = (
  formValues: FormValues<AvailabilityHoursFormConfig>,
  breaksByDay: Record<Weekday, string[][]>
): Partial<Schedule> => {
  const newSchedule: Partial<Schedule> = {};

  DAYS_OF_WEEK.forEach((day) => {
    const isChecked = formValues[`${day}-checkbox`];
    const timeRange = formValues[`${day}-time-range`];

    if (isChecked && timeRange && timeRange[0] && timeRange[1]) {
      newSchedule[day] = [
        {
          startTime: removeSecondsFromTimeFormat(timeRange[0]),
          endTime: removeSecondsFromTimeFormat(timeRange[1]),
          ...(breaksByDay[day].length
            ? {
                breaks: breaksByDay[day].map((breakTime) => ({
                  startTime: removeSecondsFromTimeFormat(breakTime[0]),
                  endTime: removeSecondsFromTimeFormat(breakTime[1]),
                })),
              }
            : undefined),
        },
      ];
    }
  });

  return newSchedule;
};

function checkIsOverlappingBreak(breaks: string[][], newBreak: string[], ignoreIndex = -1): boolean {
  const [x, y] = newBreak;

  for (let i = 0; i < breaks.length; i++) {
    if (i === ignoreIndex) continue; // Skip the ignored index

    const [start, end] = breaks[i];

    if (x < end && y > start) {
      return true; // Overlap found
    }
  }
  return false; // No overlap
}

export type ValidateBreakFn = ReturnType<typeof validateBreak>;

export const validateBreak =
  (openHoursByDay: FormValues<AvailabilityHoursFormConfig>) =>
  (day: Weekday, breaks: string[][], newBreak: string[], ignoreIndex = -1): string => {
    // Check if the new break overlaps with existing breaks
    const isOverlappingBreak = checkIsOverlappingBreak(breaks, newBreak, ignoreIndex);

    if (isOverlappingBreak) {
      return i18next.t('Break overlaps with another break', { ns: 'schedule' });
    }

    // check if the new break is within the open hours
    const [start, end] = newBreak;
    const [openTime, closeTime] = openHoursByDay[`${day}-time-range`] ?? [];
    if (start < openTime || end > closeTime) {
      return i18next.t('Break is outside of scheduled hours', { ns: 'schedule' });
    }

    return '';
  };
